/**
 * useSavedRituals Hook
 * Fetches and manages saved rituals for the current user
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { getSavedRituals, toggleSavedRitual } from "../services/saved-rituals.service"

interface UseSavedRitualsOptions {
  page?: number
  limit?: number
  search?: string
  enabled?: boolean
}

/**
 * Hook for fetching saved rituals
 */
export function useSavedRituals(options?: UseSavedRitualsOptions) {
  return useQuery({
    queryKey: ["saved-rituals", options?.page, options?.limit, options?.search],
    queryFn: () =>
      getSavedRituals({
        page: options?.page,
        limit: options?.limit,
        search: options?.search,
      }),
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook for toggling ritual save status
 * Invalidates saved rituals list, profile statistics, and related queries on success
 */
export function useToggleRitualSave() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ritualId: string) => toggleSavedRitual(ritualId),
    onSuccess: (_data, variables) => {
      // Invalidate saved rituals list
      queryClient.invalidateQueries({
        queryKey: ["saved-rituals"],
      })

      // Invalidate profile statistics (saved rituals count)
      queryClient.invalidateQueries({
        queryKey: ["profileStatistics"],
      })
      queryClient.invalidateQueries({
        queryKey: ["publicProfileStatistics"],
      })

      // Invalidate ritual detail queries
      queryClient.invalidateQueries({
        queryKey: ["ritual", variables],
      })

      // Invalidate user ritual practices, teachings, and coachings lists
      // Use predicate function to match all queries starting with these keys
      queryClient.invalidateQueries({
        predicate: query => {
          const key = query.queryKey[0] as string | undefined
          return (
            key === "user-ritual-practices" ||
            key === "user-ritual-teachings" ||
            key === "user-ritual-coachings"
          )
        },
      })

      // Invalidate community rituals queries
      queryClient.invalidateQueries({
        queryKey: ["community-rituals"],
      })
    },
  })
}
